import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Award, BookOpen, Star, StarHalf, Book, Home } from 'lucide-react';
import { asset } from '../../../shared/utils/asset';
import { SEO } from '../../../shared/components';

// --- Interface Definitions ---
interface TestimonialCardProps {
  rating: number;
  text: string;
  name: string;
  role: string;
  initials: string;
  className?: string;
}

interface SuccessStoryProps {
  name: string;
  exam: string;
  percentage: string;
  testimonial: string;
  initial: string;
  className?: string;
}

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  className?: string;
}

interface ProgramCardProps {
  tag: string;
  title: string;
  description: string;
  link: string;
  className?: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description, className = "" }) => {
  const renderIconWithStyles = () => {
    if (React.isValidElement(icon)) {
      const iconElement = icon as React.ReactElement<{ className?: string }>;
      const existingClassName = iconElement.props.className || '';
      return React.cloneElement(iconElement, { className: `w-6 h-6 text-accent ${existingClassName}` });
    }
    return icon;
  };

  return (
    <div className={`p-6 bg-white rounded-xl shadow-lg ${className}`}>
      <div className="w-12 h-12 flex items-center justify-center rounded-full mb-4 bg-accent/10">
        {renderIconWithStyles()}
      </div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-muted-foreground">{description}</p>
    </div>
  );
};

const ProgramCard: React.FC<ProgramCardProps> = ({ tag, title, description, link, className = "" }) => (
  <div className={`card p-8 transform transition-all duration-300 hover:-translate-y-1 ${className}`}>
    <span className="text-sm font-medium text-muted-foreground mb-4 block">{tag}</span>
    <h3 className="text-2xl font-semibold text-foreground mb-3">{title}</h3>
    <p className="text-muted-foreground mb-6">{description}</p>
    <Link to={link} className="text-accent font-semibold inline-flex items-center group">
      Learn more
      <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
  </div>
);

const SuccessStory: React.FC<SuccessStoryProps> = ({ name, exam, percentage, testimonial, initial, className = "" }) => (
  <div className={`card p-8 ${className}`}>
    <div className="flex items-start gap-4 mb-6">
      <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
        <span className="text-lg font-bold text-accent">{initial}</span>
      </div>
      <div>
        <h3 className="text-lg font-semibold">{name}</h3>
        <p className="text-sm text-muted-foreground">{exam}</p>
      </div>
    </div>
    <div className="mb-4">
      <span className="text-5xl font-display font-bold text-foreground">{percentage}</span>
    </div>
    <p className="text-base text-muted-foreground">"{testimonial}"</p>
  </div>
);

const TestimonialCard: React.FC<TestimonialCardProps> = ({ rating, text, name, role, initials, className = "" }) => (
  <div className={`card p-8 ${className}`}>
    <div className="flex items-center mb-4">
      {[...Array(Math.floor(rating))].map((_, i) => (
        <Star key={i} className="w-4 h-4 text-accent fill-accent" />
      ))}
      {rating % 1 !== 0 && <StarHalf className="w-4 h-4 text-accent" />}
    </div>
    <p className="text-base text-foreground mb-6 leading-relaxed">"{text}"</p>
    <div className="flex items-center">
      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mr-3 flex-shrink-0">
        <span className="font-medium text-foreground">{initials}</span>
      </div>
      <div>
        <h4 className="font-semibold text-foreground">{name}</h4>
        <p className="text-sm text-muted-foreground">{role}</p>
      </div>
    </div>
  </div>
);

/**
 * Home White Variant - Pure white background for all sections
 * Route: /home-white
 */
const HomeWhite: React.FC = () => {
  const features = [
    { icon: <Users />, title: 'Personalized Approach', description: "We create individualized learning plans tailored to each student's unique needs, learning style, and goals." },
    { icon: <Award />, title: 'Expert Teachers', description: 'Our team consists of qualified educators with extensive experience in their subject areas and passion for teaching.' },
    { icon: <BookOpen />, title: 'Multiple Curricula', description: 'We support various curricula including CBSE, IGCSE, IB, Federal, and Bangladesh Board to meet diverse educational needs.' },
  ];

  const programs = [
    { tag: 'Grades K.G-12', title: 'Core Subjects', description: 'Mathematics, Physics, Chemistry, Biology, English, and more tailored to your curriculum.', link: '/services' },
    { tag: 'All Levels', title: 'Specialized Programs', description: 'Crash courses, entrance test preparation, and foundation courses for advanced learning.', link: '/services' },
    { tag: 'Summer', title: 'Summer Courses', description: 'Keep the learning momentum going during summer with our engaging and enriching programs.', link: '/services' },
  ];

  const successStories = [
    { name: "Lalenthika", exam: "CBSE Board Exam", percentage: "93.4%", testimonial: "Tuition Point helped me understand complex concepts and gave me the confidence to excel in my board exams.", initial: "L" },
    { name: "Keerthana", exam: "CBSE Board Exam", percentage: "93%", testimonial: "The personalized attention from teachers at Tuition Point made all the difference in my academic performance.", initial: "K" }
  ];

  const testimonials = [
    { rating: 5, text: "I've seen remarkable improvement in my daughter's academic skills over just 3 months at Tuition Point.", name: "Ponnu Menu", role: "Parent", initials: "PM" },
    { rating: 5, text: "The worksheets and teaching methods at Tuition Point have really helped my child grasp difficult concepts.", name: "Aatha Sharma", role: "Parent", initials: "AS" },
    { rating: 4.5, text: "Thanks to the coaching at Tuition Point, my kids gained admission to BPS.", name: "Kesavan Nagaraj", role: "Parent", initials: "KN" }
  ];

  const quickLinks = [
    { icon: <Book className="w-6 h-6 text-cyan-700" />, title: "Curriculum", description: "Explore the various curricula we support.", link: "/curriculum" },
    { icon: <Users className="w-6 h-6 text-cyan-700" />, title: "Parent & Student Support", description: "Learn about our comprehensive support systems.", link: "/parent-support" },
    { icon: <Home className="w-6 h-6 text-cyan-700" />, title: "Our Campus", description: "Take a tour of our modern facilities.", link: "/campus" }
  ];

  // Background colors for this variant
  const bgMain = "bg-white";
  const bgAlt = "bg-white";

  return (
    <div className={`${bgMain} text-foreground min-h-screen`}>
      <SEO title="Home White Variant | Tuition Point Qatar" />

      {/* Demo Label */}
      <div className="bg-gray-800 text-white text-center py-2 text-sm font-medium">
        🎨 Variant: <strong>WHITE</strong> - All sections use white background
      </div>

      {/* Variant Navigation */}
      <div className="bg-gray-100 py-3 border-b">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="flex justify-center gap-3 flex-wrap">
            <Link to="/home-white" className="px-3 py-1.5 bg-white border-2 border-gray-800 rounded font-medium text-sm">White ●</Link>
            <Link to="/home-light-blue" className="px-3 py-1.5 bg-sky-100 border border-sky-300 rounded font-medium text-sm hover:bg-sky-200">Light Blue</Link>
            <Link to="/home-blue" className="px-3 py-1.5 bg-cyan-200 border border-cyan-400 rounded font-medium text-sm hover:bg-cyan-300">Blue</Link>
            <Link to="/home-dark-blue" className="px-3 py-1.5 bg-primary text-white rounded font-medium text-sm hover:opacity-90">Dark Blue</Link>
            <Link to="/" className="px-3 py-1.5 bg-gray-800 text-white rounded font-medium text-sm hover:bg-gray-900">← Main</Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className={`pt-16 pb-20 md:pt-20 md:pb-24 overflow-hidden ${bgMain}`}>
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium text-muted-foreground mb-2 block">Licensed by the Ministry of Education & Higher Education</span>
              <h1 className="text-foreground text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
                Expert K-12 Tutoring in <br/>Al Wakra, Qatar
              </h1>
              <p className="text-lg text-foreground mb-8 max-w-lg">
                Welcome to <span className="brand-orange bg-yellow-100 rounded-full p-1 px-3">Tuition Point Education Center</span>. We provide personalized tutoring for CBSE, IGCSE, IB, Federal & Bangladesh Board students.
              </p>
              <div className="flex items-center gap-4">
                <Link to="/enrollment" className="bg-cyan-500 hover:bg-cyan-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md transition duration-200 flex items-center">
                  Enroll Today <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
                <Link to="/contact" className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-6 rounded-lg shadow-md transition duration-200">
                  Contact Us
                </Link>
              </div>
            </div>
            <div>
              <img src={asset('Home/home.jpg')} alt="Students studying" className="w-full h-auto rounded-2xl shadow-lg" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Why Choose Tuition Point Qatar?</h2>
            <p className="text-lg text-white">Al Wakra's trusted education center for CBSE, IGCSE, and IB students.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <FeatureCard key={index} icon={feature.icon} title={feature.title} description={feature.description} />
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className={`py-24 ${bgAlt}`}>
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-semibold text-foreground mb-4">Our Programs</h2>
            <p className="text-lg text-muted-foreground">Comprehensive academic support for students at every level.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <ProgramCard key={index} tag={program.tag} title={program.title} description={program.description} link={program.link} />
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-semibold text-white mb-4">Success Stories</h2>
            <p className="text-lg text-white">Our students achieve remarkable results.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {successStories.map((story, index) => (
              <SuccessStory key={index} {...story} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className={`py-24 ${bgAlt}`}>
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <div className="flex items-center justify-center mb-6">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-accent fill-accent" />)}
              <span className="ml-2 text-lg font-medium text-muted-foreground">4.9/5</span>
            </div>
            <h2 className="text-4xl font-semibold text-foreground mb-4">What Parents Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((t, index) => (
              <TestimonialCard key={index} {...t} />
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-24 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-semibold text-white mb-4">Explore More</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {quickLinks.map((link, index) => (
              <Link to={link.link} key={index} className="card p-8 flex flex-col items-start hover:-translate-y-1 transition-transform">
                <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mb-6">{link.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{link.title}</h3>
                <p className="text-muted-foreground">{link.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeWhite;
